// PMS MRN lookup screen.
// Workflow: admin/pharmacist/nurse types an MRN -> we hit the mocked PMS
// adapter -> show profile + active meds + current delivery preference. If the
// patient already exists in M2B, the edit form lets them flip the delivery
// preference to M2B and update the room. Saves audit-logged via PATCH.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

const PREF_LABEL = {
  m2b:    'Meds-to-Beds (in-room delivery)',
  pickup: 'Patient pickup at pharmacy',
  mail:   'Mail order',
  none:   'No preference set',
};

const STATUS_BADGE = {
  new: 'bg-slate-200 text-slate-800',
  received: 'bg-blue-100 text-blue-800',
  adjudicated: 'bg-indigo-100 text-indigo-800',
  rejected: 'bg-red-100 text-red-800',
  filled: 'bg-amber-100 text-amber-800',
  verified: 'bg-emerald-100 text-emerald-800',
  dispensed: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-emerald-200 text-emerald-900',
};

export default function AdminLookup() {
  const tHref = useTenantHref();
  const { user } = useAuth();
  const [mrn, setMrn] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState(null);

  async function search(e) {
    e?.preventDefault?.();
    if (!mrn.trim()) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await api.pmsLookup(mrn.trim());
      setResult(r);
      setRecent(prev => [{ mrn: mrn.trim().toUpperCase(), at: new Date(), found: r.found }, ...prev].slice(0, 8));
    } catch (e) {
      setErr(e.body?.message || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">PMS Patient Lookup</h1>
        <p className="text-sm text-slate-500">
          Search the pharmacy management system by MRN to view a patient's profile, active medications,
          and current delivery preference. You can switch their delivery to <strong>Meds-to-Beds</strong>{' '}
          and set the room from here.
        </p>
      </div>

      <form onSubmit={search} className="card p-4 flex flex-col sm:flex-row gap-3 sm:gap-2 sm:items-end">
        <label className="block text-sm flex-1">
          <span className="text-xs text-slate-600">Medical Record Number (MRN)</span>
          <input className="input font-mono" placeholder="e.g. MRN1001" value={mrn}
                 onChange={e => setMrn(e.target.value)} />
        </label>
        <button className="btn-primary" disabled={busy}>{busy ? 'Searching…' : 'Search PMS'}</button>
      </form>

      <div className="text-xs text-slate-500">
        Try: <code className="px-1 bg-slate-100 rounded">MRN1001</code>{' '}
        <code className="px-1 bg-slate-100 rounded">MRN1003</code>{' '}
        <code className="px-1 bg-slate-100 rounded">MRN3001</code>{' '}
        <code className="px-1 bg-slate-100 rounded">MRN3002</code> (PMS-only, not yet enrolled in M2B)
      </div>

      {err && <div className="card p-4 text-red-600 text-sm">{err}</div>}

      {result && !result.found && (
        <div className="card p-4 border-l-4 border-amber-500 bg-amber-50">
          <div className="font-medium text-amber-900">No PMS record for {result.mrn}</div>
          <div className="text-sm text-amber-800">Double-check the MRN. The PMS system has no patient with that identifier.</div>
        </div>
      )}

      {result?.found && <ResultPanel result={result} role={user?.role} onUpdated={() => search()} />}

      {recent.length > 0 && (
        <div className="card p-4">
          <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">Recent lookups</div>
          <ul className="text-sm space-y-1">
            {recent.map((r, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="font-mono text-slate-700">{r.mrn}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${r.found ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                  {r.found ? 'found' : 'not found'}
                </span>
                <span className="text-xs text-slate-500">{r.at.toLocaleTimeString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ResultPanel({ result, role, onUpdated }) {
  const p = result.profile;
  const m2b = result.m2b_record;
  const enrolled = !!m2b;

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="text-xs uppercase text-slate-500 tracking-wide">PMS Profile · source: {result.source}</div>
            <h2 className="text-xl font-bold">{p.first_name} {p.last_name}</h2>
            <div className="text-sm text-slate-600">
              MRN <span className="font-mono">{p.mrn}</span> · DOB {p.dob} · {p.phone}
            </div>
            <div className="text-sm text-slate-600">{p.address}</div>
          </div>
          <div className="text-left sm:text-right shrink-0">
            <div className={`text-xs px-2 py-1 rounded inline-block ${enrolled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
              {enrolled ? 'Enrolled in M2B' : 'Not yet in M2B'}
            </div>
            {enrolled && (
              <div className="mt-1">
                <Link to={tHref(`/patients/${m2b.id}`)} className="text-xs hover:underline" style={{ color: 'var(--brand-primary)' }}>
                  Open in M2B →
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <InfoBlock label="Insurance">
            {p.insurance ? (
              <div className="text-sm">
                <div className="font-medium">{p.insurance.plan}</div>
                <div className="text-slate-600 font-mono text-xs">
                  BIN {p.insurance.bin} · PCN {p.insurance.pcn} · GRP {p.insurance.group}
                </div>
              </div>
            ) : <span className="text-slate-500 text-sm">—</span>}
          </InfoBlock>
          <InfoBlock label="Current delivery preference (PMS)">
            <div className="text-sm font-medium">{PREF_LABEL[p.delivery_preference] || p.delivery_preference}</div>
            {p.preferred_room && <div className="text-xs text-slate-600">Room: <span className="font-mono">{p.preferred_room}</span></div>}
          </InfoBlock>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-medium">Active Medications</div>
          <div className="text-xs text-slate-500">{p.active_meds.length} on file</div>
        </div>
        <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2">Rx #</th>
              <th className="text-left px-4 py-2">Drug</th>
              <th className="text-left px-4 py-2">Sig</th>
              <th className="text-left px-4 py-2">Qty / Days</th>
              <th className="text-left px-4 py-2">Prescriber</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {p.active_meds.map(m => (
              <tr key={m.rx_number} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono text-slate-700">{m.rx_number}</td>
                <td className="px-4 py-2">
                  {m.drug}
                  {m.high_risk && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">high-risk</span>}
                </td>
                <td className="px-4 py-2 text-slate-600">{m.sig}</td>
                <td className="px-4 py-2 text-slate-600">{m.qty} / {m.days}d</td>
                <td className="px-4 py-2 text-slate-600">
                  {m.prescriber?.name}
                  <span className="block text-xs text-slate-400 font-mono">NPI {m.prescriber?.npi}</span>
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[m.status] || 'bg-slate-200'}`}>{m.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        </div>
      </div>

      {enrolled
        ? <EditPanel patientId={m2b.id} initial={m2b} onUpdated={onUpdated} role={role} />
        : <div className="card p-4 bg-slate-50 text-sm text-slate-700">
            This patient exists in the PMS but has not been admitted/enrolled in M2B yet.
            They'll appear here once an EHR <strong>ADT^A01</strong> admission message is received.
            (Admin → Integration Simulator can simulate this for testing.)
          </div>
      }
    </div>
  );
}

function InfoBlock({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500 tracking-wide mb-1">{label}</div>
      {children}
    </div>
  );
}

function EditPanel({ patientId, initial, onUpdated, role }) {
  const [pref, setPref] = useState(initial.delivery_preference || 'm2b');
  const [room, setRoom] = useState(initial.room_number || '');
  const [m2b, setM2b] = useState(!!initial.m2b_eligible);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const eligibilityChanged = m2b !== !!initial.m2b_eligible;
  const dirty = pref !== initial.delivery_preference || room !== (initial.room_number || '') || eligibilityChanged;

  async function save() {
    if (eligibilityChanged && !reason.trim()) { setErr('Please provide a reason when changing M2B eligibility.'); return; }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const body = { delivery_preference: pref, room_number: room || null };
      if (eligibilityChanged) { body.m2b_eligible = m2b; body.m2b_override_reason = reason.trim(); }
      await api.patchPatient(patientId, body);
      setMsg('Saved. Audit log updated.');
      setReason('');
      onUpdated?.();
    } catch (e) {
      setErr(e.body?.message || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="font-medium">Edit M2B record</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Delivery preference</span>
          <select className="input" value={pref} onChange={e => setPref(e.target.value)}>
            <option value="m2b">Meds-to-Beds (in-room)</option>
            <option value="pickup">Pickup at pharmacy</option>
            <option value="mail">Mail order</option>
            <option value="none">None</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Room / location</span>
          <input className="input font-mono" placeholder="e.g. 4-East-22" value={room}
                 onChange={e => setRoom(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">M2B eligibility</span>
          <div className="flex items-center gap-2 mt-1.5">
            <input type="checkbox" checked={m2b} onChange={e => setM2b(e.target.checked)} />
            <span className="text-sm">{m2b ? 'Eligible' : 'Not eligible'}</span>
          </div>
        </label>
      </div>
      {eligibilityChanged && (
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Reason for eligibility change (required, audit-logged)</span>
          <input className="input" value={reason} onChange={e => setReason(e.target.value)}
                 placeholder="e.g. Patient declined / clinical exception / requested by floor" />
        </label>
      )}
      {err && <div className="text-sm text-red-600">{err}</div>}
      {msg && <div className="text-sm text-emerald-700">{msg}</div>}
      <div className="flex gap-2">
        <button className="btn-primary" disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        {!dirty && <span className="text-xs text-slate-500 self-center">No changes to save</span>}
      </div>
      <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
        Acting as <strong>{role}</strong>. Every save is recorded in the audit log with before/after state.
      </div>
    </div>
  );
}
